import { type Readable, derived, get, writable } from 'svelte/store';
import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
import { gqlClientMutations } from '#lib/gqlClientMutations.js';
import { NEWS_RESTORING_SESSION_ATTR } from '#lib/websites/news/constants/auth.js';
import { LocalStorageKeys } from '#lib/websites/news/constants/local-storage-keys.js';
import { GetCognitoUserPreferencesDocument } from '#lib/websites/news/mutations/__generated__/getCognitoUserPreferences.generated.js';
import {
	UpdateCognitoUserPreferencesDocument,
	type UpdateCognitoUserPreferencesMutationVariables,
} from '#lib/websites/news/mutations/__generated__/updateCognitoUserPreferences.generated.js';
import type { SavedTags, CognitoUser } from '#lib/websites/news/types.js';
import { hasStoredCognitoSession } from '#lib/websites/news/utils/hasStoredCognitoSession/hasStoredCognitoSession.js';
import { loadCognitoUser } from '#lib/websites/news/utils/loadCognitoUser/loadCognitoUser.js';
import { loadNewsAuth } from '#lib/websites/news/utils/loadNewsAuth/loadNewsAuth.js';
import { browser } from '$app/env';
import { createDialogStores } from '$global/components/Dialog';
import { showToast } from '$global/components/Toasts';
import { createNavPopover } from '$global/features/navigation/utils/createNavPopover';
import { safePersistedStore } from '$global/utils/safePersistedStore/safePersistedStore';
import { getAnalytics, track } from '$global/utils/track';
import {
	AuthAction,
	AuthMethod,
	AuthProvenance,
	AuthReason,
	trackAuthEvent,
} from '$global/utils/trackAuthEvent';
import type { SavedTagType } from '$gql/types-mutations';

// todo news: rename to news context
export const createAuthContext = () => {
	const successMessage = writable('');
	// Optional smaller text shown under the success message (e.g. what happens
	// next). Cleared with the message when the success dialog closes.
	const successDescription = writable('');
	const search = writable('');

	const usersToSubscribeStore = safePersistedStore(
		LocalStorageKeys.UsersToSubscribe,
		[] as string[]
	);

	const user = writable<null | CognitoUser>(null);

	const hasUserSubscribed = (email: string) =>
		get(usersToSubscribeStore).includes(email);
	const addUserToSubscribe = (email: string) => {
		if (!hasUserSubscribed(email)) {
			usersToSubscribeStore.set([...get(usersToSubscribeStore), email]);
		}
	};

	const deleteUserToSubscribe = (email: string) => {
		const index = get(usersToSubscribeStore).indexOf(email);
		if (index > -1) {
			usersToSubscribeStore.set([
				...get(usersToSubscribeStore).slice(0, index),
				...get(usersToSubscribeStore).slice(index + 1),
			]);
		}
	};

	const savedTagsDrawerStores = createDialogStores();
	const unsubscribeDialogStores = createDialogStores();
	const newsletterDialogStores = createDialogStores();
	const loginDialogStores = createDialogStores();
	const createAccountDialogStores = createDialogStores();
	const accountVerificationCodeDialogStores = createDialogStores();
	const passwordVerificationCodeDialogStores = createDialogStores();
	const requestPasswordResetDialogStores = createDialogStores();
	const passwordResetVerificationCodeDialogStores = createDialogStores();
	const confirmNewPasswordDialogStores = createDialogStores();
	const changePasswordDialogStores = createDialogStores();
	const closeAccountDialogStores = createDialogStores();
	const successDialogStores = createDialogStores();

	const mobileMenuPopover = createNavPopover();

	const showOverlay = derived(
		[
			unsubscribeDialogStores.states.open,
			newsletterDialogStores.states.open,
			loginDialogStores.states.open,
			createAccountDialogStores.states.open,
			accountVerificationCodeDialogStores.states.open,
			successDialogStores.states.open,
			requestPasswordResetDialogStores.states.open,
			confirmNewPasswordDialogStores.states.open,
			passwordResetVerificationCodeDialogStores.states.open,
			changePasswordDialogStores.states.open,
			closeAccountDialogStores.states.open,
			passwordVerificationCodeDialogStores.states.open,
		],
		(openStores) => {
			return openStores.some((openStore) => openStore);
		}
	);

	const passwordVerificationCodeError = writable('');

	const accessToken = derived(
		user,
		($user) => $user?.signInUserSession?.accessToken?.jwtToken
	);

	const hasLoadedCredentials = writable(false);
	const hasLoadedUserPrefs = writable(false);
	// Whether a Cognito session is still being restored, i.e. whether prefs for a
	// signed-in user are on their way. Consumers that must not flash logged-out
	// content (the saved-tags bar) wait on this rather than on
	// `hasLoadedCredentials`, which says only that the *check* has finished.
	//
	// Seeded from storage at construction rather than in NewsHeader's mount
	// effect: the bar renders in the same layout and would otherwise get one
	// paint with this still `false`, flashing the default tags at a signed-in
	// user — the exact swap it is meant to avoid. False during SSR, where there
	// is no storage to read and the markup must match the signed-out render.
	const isRestoringSession = writable(browser && hasStoredCognitoSession());
	const refresh = writable(0);
	const updatingUserTags = writable('');

	// The initial value matters: this derived is lazy and, once a user is logged
	// in, only calls `set` after an awaited request. Its first subscription can
	// happen at the moment of login (nothing subscribes while logged out), and
	// without an initial value `$usersPrefs` is `undefined` there — so
	// `$usersPrefs.savedTags` throws mid-flush and breaks all news navigation
	// until a full reload.
	const usersPrefs = derived(
		[accessToken, refresh],
		(async (
			[$accessToken, $refresh]: [string | undefined, number],
			set: (userPrefs: {
				savedTags: SavedTags | null;
				newsletterOptIn: boolean | null | undefined;
				refresh: number;
			}) => void
		) => {
			if (!$accessToken) {
				set({ savedTags: null, newsletterOptIn: null, refresh: $refresh });
			} else {
				try {
					const response = await gqlClientMutations.request(
						GetCognitoUserPreferencesDocument,
						{},
						{
							'x-cognito-authorization': `Bearer ${$accessToken}`,
						}
					);

					set({
						refresh: $refresh,
						newsletterOptIn:
							response?.getCognitoUserPreferences?.newsletter_opt_in,
						// De-duplicate saved tags by (id, type). The same artist/geotag
						// must appear only once — duplicates produce duplicate keys in
						// the keyed {#each} in SavedTagsBar, which throws a Svelte
						// runtime error that breaks news header navigation and search.
						savedTags: (
							response?.getCognitoUserPreferences?.saved_tags || []
						).filter(
							(savedTag, index, savedTags) =>
								index ===
								savedTags.findIndex(
									(other) =>
										other?.id === savedTag?.id && other?.type === savedTag?.type
								)
						),
					});
				} catch {
					await (await loadNewsAuth()).signOut();

					// Before op.clear() drops the profile, and flagged as forced so it
					// does not inflate the voluntary logout count.
					trackAuthEvent({
						action: AuthAction.Logout,
						method: AuthMethod.Password,
						provenance: AuthProvenance.News,
						reason: AuthReason.SessionError,
					});

					getAnalytics()?.clear();
					user.set(null);
					mobileMenuPopover.close();
					savedTagsDrawerStores.states.open.set(false);
					// The restore is over — failed, but over. Without this the
					// saved-tags bar would stay withheld for the rest of the session.
					isRestoringSession.set(false);

					showToast({
						variant: 'error',
						message:
							'Something went wrong while loading your user preferences. You have been logged out.',
					});
				} finally {
					setTimeout(() => {
						updatingUserTags.set('');
					}, 0);
				}
			}

			// Guarded on the access token, not on `hasLoadedCredentials`: on a page
			// refresh `user.set()` runs *before* that flag is set (it lands in
			// loadCredentials' `finally`), and since it is not a dependency of this
			// derived nothing re-runs it — so a signed-in user's prefs were never
			// marked loaded and the saved-tags bar never swapped in their tags.
			// The logged-out pass is instead skipped while a session is still being
			// restored, which is what the old guard was really protecting against.
			if ($accessToken || !get(isRestoringSession)) {
				hasLoadedUserPrefs.set(true);
				// Whatever this pass resolved to is now the truth the bar renders, so
				// the restore window is closed.
				isRestoringSession.set(false);
			}
		}) as Parameters<typeof derived>[1],
		{ refresh: 0, savedTags: null, newsletterOptIn: null }
	) as Readable<{
		refresh: number;
		savedTags: null | SavedTags;
		newsletterOptIn: boolean | null | undefined;
	}>;

	// `usersPrefs` is a lazy derived whose callback is what fetches preferences
	// and flips `hasLoadedUserPrefs` / `isRestoringSession`. Those flags gate UI
	// that is itself conditionally rendered (the saved-tags bar is hidden while a
	// session is being restored), so leaving the subscription to consumers
	// deadlocks: nothing renders, so nothing subscribes, so the derived never
	// runs, so the flags never flip. Every other reader lives in a dialog, drawer
	// or card that may never open.
	//
	// Holding one subscription here makes the fetch and the flags depend on the
	// context's own lifetime instead of on which components happen to be mounted.
	// Not unsubscribed: the context lives exactly as long as the page that
	// created it.
	if (browser) {
		usersPrefs.subscribe(() => {});

		// Counterpart of the pre-paint probe in NewsLayout: the probe stamps this
		// attribute on <html> before first paint so CSS hides the saved-tags bar
		// instead of flashing the server-rendered default tags at a signed-in
		// visitor. Every path that ends the restore — prefs loaded, no session
		// after all, restore failed — flips `isRestoringSession` to false, so this
		// one subscription hands visibility back to the client-side gate no matter
		// how the restore concluded. Runs immediately on subscribe too, which for
		// a signed-out visitor removes a stale attribute left by a probe whose
		// stored session has since been cleared elsewhere.
		isRestoringSession.subscribe(($isRestoringSession) => {
			if (!$isRestoringSession) {
				document.documentElement.removeAttribute(NEWS_RESTORING_SESSION_ATTR);
			}
		});
	}

	const updateUserTags = async (
		variables: UpdateCognitoUserPreferencesMutationVariables,
		name: string
	) => {
		updatingUserTags.set(name);

		try {
			const userObj = await loadCognitoUser();
			user.set(userObj);

			await gqlClientMutations.request(
				UpdateCognitoUserPreferencesDocument,
				variables,
				{
					'x-cognito-authorization': `Bearer ${get(accessToken)}`,
				}
			);

			refresh.update(($refresh) => $refresh + 1);

			return { success: true as const };
		} catch (error) {
			await (await loadNewsAuth()).signOut();

			// Before op.clear() drops the profile, and flagged as forced so it
			// does not inflate the voluntary logout count.
			trackAuthEvent({
				action: AuthAction.Logout,
				method: AuthMethod.Password,
				provenance: AuthProvenance.News,
				reason: AuthReason.SessionError,
			});

			getAnalytics()?.clear();
			user.set(null);
			mobileMenuPopover.close();
			savedTagsDrawerStores.states.open.set(false);

			showToast({
				variant: 'error',
				message:
					'Something went wrong while updating your user preferences. You have been logged out.',
			});

			// Returned rather than rethrown: callers already rely on this resolving,
			// but they cannot otherwise tell a failed update from a successful one.
			return { success: false as const, error };
		}
	};

	const removeUserTag = async ({
		id,
		name,
		type,
	}: {
		id: string;
		type: SavedTagType;
		name: string;
	}) => {
		track(NewsMixpanelEvents.DeleteTag, {
			value: name,
			type,
		});

		return updateUserTags(
			{
				cognitoUserPreferences: {
					newsletter_opt_in: get(usersPrefs).newsletterOptIn,
					saved_tags: get(usersPrefs)
						.savedTags?.filter((savedTag) => savedTag?.id !== id)
						?.map((savedTag) => ({
							id: savedTag?.id as string,
							type: savedTag?.type as SavedTagType,
						})),
				},
			},
			name
		);
	};

	const addUserTag = async (userPrefToAdd: {
		id: string;
		type: SavedTagType;
		name: string;
	}) => {
		track(NewsMixpanelEvents.SaveTag, {
			value: userPrefToAdd.name,
			type: userPrefToAdd.type,
		});

		return updateUserTags(
			{
				cognitoUserPreferences: {
					newsletter_opt_in: get(usersPrefs).newsletterOptIn,
					saved_tags: [...(get(usersPrefs).savedTags || []), userPrefToAdd]
						?.map((savedTag) => ({
							id: savedTag?.id as string,
							type: savedTag?.type as SavedTagType,
						}))
						// Drop duplicates so re-saving an already-saved tag is a no-op.
						.filter(
							(savedTag, index, savedTags) =>
								index ===
								savedTags.findIndex(
									(other) =>
										other.id === savedTag.id && other.type === savedTag.type
								)
						),
				},
			},
			userPrefToAdd.name
		);
	};

	const toggleNewsletterPref = async () => {
		return updateUserTags(
			{
				cognitoUserPreferences: {
					newsletter_opt_in: !get(usersPrefs).newsletterOptIn,
					saved_tags: (get(usersPrefs).savedTags || [])?.map((savedTag) => ({
						id: savedTag?.id as string,
						type: savedTag?.type as SavedTagType,
					})),
				},
			},
			'newsletter'
		);
	};

	const showSavedTags = derived(
		[hasLoadedUserPrefs, user, usersPrefs],
		([$hasLoadedUserPrefs, $user, $usersPrefs]) =>
			$hasLoadedUserPrefs &&
			(!$user || ($user && $usersPrefs.savedTags?.length))
	);

	return {
		search,
		user,
		usersPrefs,
		showSavedTags,
		updatingUserTags,
		hasLoadedCredentials,
		hasLoadedUserPrefs,
		isRestoringSession,
		toggleNewsletterPref,
		removeUserTag,
		addUserTag,
		showOverlay,
		successMessage,
		successDescription,
		addUserToSubscribe,
		deleteUserToSubscribe,
		hasUserSubscribed,
		mobileMenuPopover,
		savedTagsDrawerStores,
		unsubscribeDialogStores,
		successDialogStores,
		loginDialogStores,
		createAccountDialogStores,
		passwordVerificationCodeDialogStores,
		accountVerificationCodeDialogStores,
		requestPasswordResetDialogStores,
		passwordResetVerificationCodeDialogStores,
		confirmNewPasswordDialogStores,
		changePasswordDialogStores,
		closeAccountDialogStores,
		passwordVerificationCodeError,
		usersToSubscribeStore,
		newsletterDialogStores,
	};
};
